import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Company,
  CompanyFilters,
  CompaniesState,
  SavedFilter,
} from '@/types/company.types';

const initialState: CompaniesState = {
  list: [],
  totalCount: 0,
  loading: false,
  error: null,
  filters: {},
  savedFilters: [],
  marketingChannels: [],
  deleteCompany: { loading: false, error: null },
  disableCompany: { loading: false, error: null },
  demoCompany: { loading: false, error: null },
  extendTrial: { loading: false, error: null },
  exportCsv: { loading: false, error: null },
};

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    // Fetch companies
    fetchCompaniesRequest(
      state,
      _action: PayloadAction<{
        page: number;
        limit: number;
        filters: CompanyFilters;
        sortField?: string;
        sortOrder?: number;
        search?: string;
      }>,
    ) {
      state.loading = true;
      state.error = null;
    },
    fetchCompaniesSuccess(
      state,
      action: PayloadAction<{ list: Company[]; totalCount: number }>,
    ) {
      state.loading = false;
      state.list = action.payload.list;
      state.totalCount = action.payload.totalCount;
    },
    fetchCompaniesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // Filters
    setFilters(state, action: PayloadAction<CompanyFilters>) {
      state.filters = action.payload;
    },
    clearFilters(state) {
      state.filters = {};
    },

    // Saved filters
    fetchSavedFiltersRequest(_state) {
      // loading handled by UI
    },
    fetchSavedFiltersSuccess(state, action: PayloadAction<SavedFilter[]>) {
      state.savedFilters = action.payload;
    },
    fetchSavedFiltersFailure(_state, _action: PayloadAction<string>) {
      // non-critical; error is logged but state is not changed
    },

    saveFilterRequest(_state, _action: PayloadAction<{ name: string; filters: CompanyFilters }>) {},
    saveFilterSuccess(state, action: PayloadAction<SavedFilter>) {
      state.savedFilters.push(action.payload);
    },
    saveFilterFailure(_state, _action: PayloadAction<string>) {
      // non-critical; error is logged but state is not changed
    },

    deleteFilterRequest(_state, _action: PayloadAction<string>) {},
    deleteFilterSuccess(state, action: PayloadAction<string>) {
      state.savedFilters = state.savedFilters.filter((f) => f._id !== action.payload);
    },
    deleteFilterFailure(_state, _action: PayloadAction<string>) {
      // non-critical; error is logged but state is not changed
    },

    // Marketing channels (for filter options)
    fetchMarketingChannelsRequest() {},
    fetchMarketingChannelsSuccess(state, action: PayloadAction<string[]>) {
      state.marketingChannels = action.payload;
    },
    fetchMarketingChannelsFailure(_state, _action: PayloadAction<string>) {
      // non-critical; error is logged but state is not changed
    },

    // Company actions
    deleteCompanyRequest(state, _action: PayloadAction<{ companyId: string }>) {
      state.deleteCompany = { loading: true, error: null };
    },
    deleteCompanySuccess(state, action: PayloadAction<string>) {
      state.deleteCompany = { loading: false, error: null };
      state.list = state.list.filter((c) => c._id !== action.payload);
      state.totalCount -= 1;
    },
    deleteCompanyFailure(state, action: PayloadAction<string>) {
      state.deleteCompany = { loading: false, error: action.payload };
    },

    disableCompanyRequest(
      state,
      _action: PayloadAction<{ companyId: string; isDisabled: boolean }>,
    ) {
      state.disableCompany = { loading: true, error: null };
    },
    disableCompanySuccess(state) {
      state.disableCompany = { loading: false, error: null };
    },
    disableCompanyFailure(state, action: PayloadAction<string>) {
      state.disableCompany = { loading: false, error: action.payload };
    },

    demoCompanyRequest(state, _action: PayloadAction<{ companyId: string; isDemo: boolean }>) {
      state.demoCompany = { loading: true, error: null };
    },
    demoCompanySuccess(state) {
      state.demoCompany = { loading: false, error: null };
    },
    demoCompanyFailure(state, action: PayloadAction<string>) {
      state.demoCompany = { loading: false, error: action.payload };
    },

    extendTrialRequest(state, _action: PayloadAction<{ companyId: string; days?: number }>) {
      state.extendTrial = { loading: true, error: null };
    },
    extendTrialSuccess(state) {
      state.extendTrial = { loading: false, error: null };
    },
    extendTrialFailure(state, action: PayloadAction<string>) {
      state.extendTrial = { loading: false, error: action.payload };
    },

    // CSV export
    exportCsvRequest(state, _action: PayloadAction<CompanyFilters>) {
      state.exportCsv = { loading: true, error: null };
    },
    exportCsvSuccess(state) {
      state.exportCsv = { loading: false, error: null };
    },
    exportCsvFailure(state, action: PayloadAction<string>) {
      state.exportCsv = { loading: false, error: action.payload };
    },
  },
});

export const {
  fetchCompaniesRequest,
  fetchCompaniesSuccess,
  fetchCompaniesFailure,
  setFilters,
  clearFilters,
  fetchSavedFiltersRequest,
  fetchSavedFiltersSuccess,
  fetchSavedFiltersFailure,
  saveFilterRequest,
  saveFilterSuccess,
  saveFilterFailure,
  deleteFilterRequest,
  deleteFilterSuccess,
  deleteFilterFailure,
  fetchMarketingChannelsRequest,
  fetchMarketingChannelsSuccess,
  fetchMarketingChannelsFailure,
  deleteCompanyRequest,
  deleteCompanySuccess,
  deleteCompanyFailure,
  disableCompanyRequest,
  disableCompanySuccess,
  disableCompanyFailure,
  demoCompanyRequest,
  demoCompanySuccess,
  demoCompanyFailure,
  extendTrialRequest,
  extendTrialSuccess,
  extendTrialFailure,
  exportCsvRequest,
  exportCsvSuccess,
  exportCsvFailure,
} = companiesSlice.actions;

export default companiesSlice.reducer;
